import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "@/components/icons";
import { fetchAuthProviders } from "@/lib/api";

// Human-readable messages for the ?error=... codes the Google callback appends
// when it redirects back to /login on failure.
const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  google_disabled: "Google sign-in is not enabled.",
  google_denied: "Google sign-in was cancelled.",
  google_state: "Google sign-in expired — please try again.",
  google_token: "Could not complete Google sign-in.",
  google_aud: "Could not complete Google sign-in.",
  google_iss: "Could not complete Google sign-in.",
  google_expired: "Google sign-in expired — please try again.",
  google_email: "Your Google account has no verified email.",
  google_account: "No matching account for this Google email. Contact an admin.",
  account_disabled: "This account is disabled.",
  session: "Could not start a session — please try again.",
};

export function LoginPage() {
  const nav = useNavigate();
  const location = useLocation() as any;
  const from = location.state?.from?.pathname || "/";

  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Which external providers to offer (public, pre-session).
  const providersQ = useQuery({ queryKey: ["auth-providers"], queryFn: fetchAuthProviders });
  const googleEnabled = providersQ.data?.google_login ?? false;
  const registrationEnabled = providersQ.data?.phone_registration ?? false;

  // Surface an error code the Google callback may have appended to the URL.
  React.useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("error");
    if (code) {
      toast.error(GOOGLE_ERROR_MESSAGES[code] || "Sign-in failed.");
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  function signInWithGoogle() {
    // Full-page navigation: the backend redirects to Google and back.
    const next = encodeURIComponent(from);
    window.location.href = `/auth/google/login?next=${next}`;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.set("username", username);
      body.set("password", password);

      const res = await fetch("/login", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
        credentials: "include",
        redirect: "follow",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Login failed (${res.status})`);
      }

      toast.success("Welcome back");
      nav(from, { replace: true });
    } catch (err: any) {
      toast.error(err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>Use your Charger Management credentials.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label>Username</Label>
                <Input className="mt-2" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
              </div>
              <div>
                <Label>Password</Label>
                <div className="relative mt-2">
                  <Input className="pr-10" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button className="w-full" type="submit" disabled={loading || !username || !password}>
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              <div className="flex items-center justify-center gap-3 text-center">
                <Link
                  to="/forgot-password"
                  className="text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  Forgot password?
                </Link>
                {registrationEnabled && (
                  <>
                    <span className="text-xs text-zinc-300 dark:text-zinc-600">·</span>
                    <Link
                      to="/register"
                      className="text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                    >
                      Have an invitation? Register
                    </Link>
                  </>
                )}
              </div>
            </form>

            {googleEnabled && (
              <>
                <div className="my-4 flex items-center gap-3 text-xs text-zinc-400">
                  <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                  or
                  <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={signInWithGoogle}
                >
                  Sign in with Google
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-400">
          If you get redirected back here, your session cookie likely expired.
        </div>
      </div>
    </div>
  );
}
